import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isNotificationRead } from "../../utils/notifications/notificationRead";
import { PAGE_SIZE } from '../../constants/pagination';
import { httpClient } from '../../services/api/api';
import { ApiEnvelope, unwrap } from "../../services/api/envelope";
import {
  ConnectionStatus,
  INotification,
  INotificationFilters,
  INotificationGroup,
  INotificationPagination,
  INotificationSettings,
  INotificationSettingsResponse,
  INotificationSort,
  INotificationStats,
  IToastNotification,
  NotificationStatus,
  NotificationType,
} from "../../types/notifications";

/**
 * Redux slice для системы уведомлений Event Master
 * Фаза 2: Система уведомлений (Неделя 7: Архитектура уведомлений)
 */

// Интерфейс состояния
interface NotificationsState {
  // Основные данные
  notifications: INotification[];
  groups: INotificationGroup[];
  settings: INotificationSettings[];
  stats: INotificationStats | null;

  // UI состояние
  isLoading: boolean;
  error: string | null;
  isRefreshing: boolean;

  // Фильтрация и сортировка
  filters: INotificationFilters;
  sort: INotificationSort;
  pagination: INotificationPagination;

  // WebSocket соединение
  connectionStatus: ConnectionStatus;
  lastConnected: Date | null;
  reconnectAttempts: number;

  // Toast уведомления
  toasts: IToastNotification[];

  // UI настройки
  ui: {
    isNotificationPanelOpen: boolean;
    isSettingsModalOpen: boolean;
    selectedNotificationId: string | null;
    groupingEnabled: boolean;
    soundEnabled: boolean;
    vibrationEnabled: boolean;
    autoMarkAsRead: boolean;
    showOnlyUnread: boolean;
  };

  // Кэш и производительность
  cache: {
    lastFetch: Date | null;
    etag: string | null;
    offlineQueue: INotification[];
  };
}

// Начальное состояние
const initialState: NotificationsState = {
  notifications: [],
  groups: [],
  settings: [],
  stats: null,
  isLoading: false,
  error: null,
  isRefreshing: false,
  filters: {
    types: [],
    status: [], // Убираем фильтр по умолчанию, чтобы избежать 400 ошибки
    priority: [],
  },
  sort: {
    field: "createdAt",
    order: "desc",
  },
  pagination: {
    page: 1,
    limit: PAGE_SIZE.DEFAULT,
    total: 0,
    hasMore: false,
  },
  connectionStatus: ConnectionStatus.DISCONNECTED,
  lastConnected: null,
  reconnectAttempts: 0,
  toasts: [],
  ui: {
    isNotificationPanelOpen: false,
    isSettingsModalOpen: false,
    selectedNotificationId: null,
    groupingEnabled: true,
    soundEnabled: true,
    vibrationEnabled: true,
    autoMarkAsRead: false,
    showOnlyUnread: false,
  },
  cache: {
    lastFetch: null,
    etag: null,
    offlineQueue: [],
  },
};

/**
 * Проводная форма `GET /notifications` (BE `findWithFilters`) под конвертом ADR-002.
 * AD-185: прежде ответ клался в стор как есть — конверт `{ success, data }` вместо списка,
 * `notifications` выходило `undefined`, и редьюсер падал на группировке.
 */
interface NotificationListWire {
  notifications: INotification[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface NotificationListPayload {
  notifications: INotification[];
  pagination: INotificationPagination;
}

// Асинхронные thunks
export const fetchNotifications = createAsyncThunk<
  NotificationListPayload,
  {
    filters?: INotificationFilters;
    sort?: INotificationSort;
    pagination?: Partial<INotificationPagination>;
  }
>(
  "notifications/fetchNotifications",
  async ({ filters, sort, pagination }, { rejectWithValue }) => {
    try {
      const params: Record<string, string> = {};

      if (filters?.types?.length) {
        params["types"] = filters.types.join(",");
      }
      if (filters?.status?.length) {
        // Бэкенд ожидает status=unread,read вместо status[]=unread
        params["status"] = filters.status.join(",");
      }
      if (filters?.priority?.length) {
        params["priority"] = filters.priority.join(",");
      }
      if (filters?.search) {
        params["search"] = filters.search;
      }
      if (sort) {
        params["sortField"] = sort.field;
        params["sortOrder"] = sort.order;
      }
      if (pagination?.page) {
        params["page"] = pagination.page.toString();
      }
      if (pagination?.limit) {
        params["limit"] = pagination.limit.toString();
      }

      const response = await httpClient.get<ApiEnvelope<NotificationListWire>>(
        "/notifications",
        { params },
      );
      const list = unwrap(response.data, "GET /notifications");
      return {
        notifications: list.notifications,
        pagination: {
          page: list.page,
          limit: list.limit,
          total: list.total,
          hasMore: list.hasMore,
        },
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Неизвестная ошибка"
      );
    }
  }
);

export const markAsRead = createAsyncThunk<string, string>(
  "notifications/markAsRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      await httpClient.post(`/notifications/${notificationId}/read`);
      return notificationId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Неизвестная ошибка"
      );
    }
  }
);

export const markAllAsRead = createAsyncThunk<void, void>(
  "notifications/markAllAsRead",
  async (_, { rejectWithValue }) => {
    try {
      await httpClient.patch('/notifications/read-all');
      return;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Неизвестная ошибка"
      );
    }
  }
);

export const deleteNotification = createAsyncThunk<string, string>(
  "notifications/deleteNotification",
  async (notificationId, { rejectWithValue }) => {
    try {
      await httpClient.delete(`/notifications/${notificationId}`);
      return notificationId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Неизвестная ошибка"
      );
    }
  }
);

export const fetchSettings = createAsyncThunk<
  INotificationSettingsResponse,
  void
>("notifications/fetchSettings", async (_, { rejectWithValue }) => {
  try {
    const response = await httpClient.get('/notifications/settings');
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Неизвестная ошибка"
    );
  }
});

export const updateSettings = createAsyncThunk<
  INotificationSettings,
  Partial<INotificationSettings>
>("notifications/updateSettings", async (settings, { rejectWithValue }) => {
  try {
    const response = await httpClient.patch('/notifications/settings', settings);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Неизвестная ошибка"
    );
  }
});

// Slice
const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // WebSocket действия
    setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
      state.connectionStatus = action.payload;
      if (action.payload === ConnectionStatus.CONNECTED) {
        state.lastConnected = new Date();
        state.reconnectAttempts = 0;
      }
    },

    incrementReconnectAttempts: (state) => {
      state.reconnectAttempts += 1;
    },

    resetReconnectAttempts: (state) => {
      state.reconnectAttempts = 0;
    },

    addNotificationFromSocket: (
      state,
      action: PayloadAction<INotification>
    ) => {
      const notification = action.payload;

      // Проверяем, не существует ли уже это уведомление
      const existingIndex = state.notifications.findIndex(
        (n) => n.id === notification.id
      );
      if (existingIndex === -1) {
        state.notifications.unshift(notification);

        // Обновляем статистику
        if (state.stats) {
          state.stats.total += 1;
          if (!isNotificationRead(notification)) {
            state.stats.unread += 1;
          }
        }
        regroup(state);
      }
    },

    // Toast уведомления
    addToast: (
      state,
      action: PayloadAction<Omit<IToastNotification, "id">>
    ) => {
      const toast: IToastNotification = {
        ...action.payload,
        id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      };
      state.toasts.push(toast);
    },

    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(
        (toast) => toast.id !== action.payload
      );
    },

    clearAllToasts: (state) => {
      state.toasts = [];
    },

    // UI действия
    toggleNotificationPanel: (state) => {
      state.ui.isNotificationPanelOpen = !state.ui.isNotificationPanelOpen;
    },

    openNotificationPanel: (state) => {
      state.ui.isNotificationPanelOpen = true;
    },

    closeNotificationPanel: (state) => {
      state.ui.isNotificationPanelOpen = false;
    },

    toggleSettingsModal: (state) => {
      state.ui.isSettingsModalOpen = !state.ui.isSettingsModalOpen;
    },

    selectNotification: (state, action: PayloadAction<string | null>) => {
      state.ui.selectedNotificationId = action.payload;
    },

    // Фильтрация и сортировка
    setFilters: (
      state,
      action: PayloadAction<Partial<INotificationFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Сброс на первую страницу
    },

    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },

    setSort: (state, action: PayloadAction<INotificationSort>) => {
      state.sort = action.payload;
      state.pagination.page = 1;
    },

    setPagination: (
      state,
      action: PayloadAction<Partial<INotificationPagination>>
    ) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    // UI настройки
    updateUISettings: (
      state,
      action: PayloadAction<Partial<typeof initialState.ui>>
    ) => {
      state.ui = { ...state.ui, ...action.payload };
    },

    // Групповые операции
    toggleGroupExpansion: (state, action: PayloadAction<string>) => {
      const group = state.groups.find((g) => g.id === action.payload);
      if (group) {
        group.isExpanded = !group.isExpanded;
      }
    },

    // Кэш
    updateCache: (
      state,
      action: PayloadAction<Partial<typeof initialState.cache>>
    ) => {
      state.cache = { ...state.cache, ...action.payload };
    },

    addToOfflineQueue: (state, action: PayloadAction<INotification>) => {
      state.cache.offlineQueue.push(action.payload);
    },

    clearOfflineQueue: (state) => {
      state.cache.offlineQueue = [];
    },

    // Очистка ошибок
    clearError: (state) => {
      state.error = null;
    },

    // Сброс состояния
    resetState: () => initialState,
  },

  extraReducers: (builder) => {
    // Загрузка уведомлений
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = action.payload.notifications;
        state.pagination = action.payload.pagination;
        state.cache.lastFetch = new Date();
        regroup(state);
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Отметка как прочитанное
    builder.addCase(markAsRead.fulfilled, (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(
        (n) => n.id === notificationId
      );
      if (notification && !isNotificationRead(notification)) {
        notification.isRead = true;
        notification.readAt = new Date();
        notification.status = NotificationStatus.READ;

        // Обновляем статистику
        if (state.stats) {
          state.stats.unread = Math.max(0, state.stats.unread - 1);
        }
        regroup(state);
      }
    });

    // Отметка всех как прочитанные
    builder.addCase(markAllAsRead.fulfilled, (state) => {
      state.notifications.forEach((notification) => {
        if (!isNotificationRead(notification)) {
          notification.isRead = true;
          notification.readAt = new Date();
          notification.status = NotificationStatus.READ;
        }
      });

      // Обновляем статистику
      if (state.stats) {
        state.stats.unread = 0;
      }
      regroup(state);
    });

    // Удаление уведомления
    builder.addCase(deleteNotification.fulfilled, (state, action) => {
      const notificationId = action.payload;
      const notificationIndex = state.notifications.findIndex(
        (n) => n.id === notificationId
      );

      if (notificationIndex !== -1) {
        const notification = state.notifications[notificationIndex];
        if (!notification) {
          return;
        }

        state.notifications.splice(notificationIndex, 1);

        // Обновляем статистику
        if (state.stats) {
          state.stats.total = Math.max(0, state.stats.total - 1);
          if (!isNotificationRead(notification)) {
            state.stats.unread = Math.max(0, state.stats.unread - 1);
          }
        }
        regroup(state);
      }
    });

    // Загрузка настроек
    builder.addCase(fetchSettings.fulfilled, (state, action) => {
      state.settings = action.payload.settings;
    });

    // Обновление настроек
    builder.addCase(updateSettings.fulfilled, (state, action) => {
      const updatedSetting = action.payload;
      const existingIndex = state.settings.findIndex(
        (s) => s.id === updatedSetting.id
      );

      if (existingIndex !== -1) {
        state.settings[existingIndex] = updatedSetting;
      } else {
        state.settings.push(updatedSetting);
      }
    });
  },
});

/**
 * Панель рисует ТОЛЬКО группы, поэтому они пересобираются после каждого изменения списка.
 * AD-185: уведомление из сокета попадало в `notifications`, но не в `groups` — счётчик
 * показывал «1», а список был пуст; группы, собранные до «прочитано», держали прежний объект.
 */
function regroup(state: NotificationsState): void {
  if (state.ui.groupingEnabled) {
    state.groups = groupNotificationsByType(state.notifications);
  }
}

// Вспомогательная функция для группировки уведомлений
function groupNotificationsByType(
  notifications: INotification[]
): INotificationGroup[] {
  const groups: Record<NotificationType, INotification[]> = {} as Record<
    NotificationType,
    INotification[]
  >;

  notifications.forEach((notification) => {
    if (!groups[notification.type]) {
      groups[notification.type] = [];
    }
    groups[notification.type].push(notification);
  });

  return Object.entries(groups).map(([type, notificationsForType]) => {
    const latestNotification = notificationsForType[0];

    return {
      id: `group-${type}`,
      type: type as NotificationType,
      count: notificationsForType.length,
      latestNotification: latestNotification!,
      notifications: notificationsForType,
      isExpanded: false,
    };
  });
}

// Экспорт действий
export const {
  setConnectionStatus,
  incrementReconnectAttempts,
  resetReconnectAttempts: resetReconnectAttemptsAction,
  addNotificationFromSocket,
  addToast,
  removeToast,
  clearAllToasts,
  toggleNotificationPanel,
  openNotificationPanel,
  closeNotificationPanel,
  toggleSettingsModal,
  selectNotification,
  setFilters,
  clearFilters,
  setSort,
  setPagination,
  updateUISettings,
  toggleGroupExpansion,
  updateCache,
  addToOfflineQueue,
  clearOfflineQueue,
  clearError,
  resetState,
} = notificationsSlice.actions;

// Дополнительные экспорты для совместимости с WebSocket сервисом
export const addNotification = addNotificationFromSocket;
export const updateNotification = addNotificationFromSocket; // Для обновления используем тот же action
export const removeNotification = (id: string) => (dispatch: any) => {
  dispatch(deleteNotification(id));
};
export const setConnectionState = setConnectionStatus;
export const resetReconnectAttempts = () => (dispatch: any) => {
  dispatch(resetReconnectAttemptsAction());
};

// Дополнительные экспорты для hook
export const togglePanel = toggleNotificationPanel;
export const setPanelOpen = (open: boolean) => (dispatch: any) => {
  if (open) {
    dispatch(openNotificationPanel());
  } else {
    dispatch(closeNotificationPanel());
  }
};
export const setSearchQuery = (query: string) => (dispatch: any) => {
  dispatch(setFilters({ search: query }));
};
export const toggleNotificationSelection = selectNotification;
export const selectAllNotifications = () => (_dispatch: any) => {
  // Массовый выбор в UI пока не реализован (одиночный selectedNotificationId)
};
export const clearSelection = () => (dispatch: any) => {
  dispatch(selectNotification(null));
};
export const deleteNotifications = (ids: string[]) => (dispatch: any) => {
  ids.forEach((id) => dispatch(deleteNotification(id)));
};

// Селекторы
export const selectNotifications = (state: {
  notifications: NotificationsState;
}) => state.notifications.notifications;
export const selectUnreadCount = (state: {
  notifications: NotificationsState;
}) => state.notifications.stats?.unread || 0;
export const selectConnectionStatus = (state: {
  notifications: NotificationsState;
}) => state.notifications.connectionStatus;
export const selectToasts = (state: { notifications: NotificationsState }) =>
  state.notifications.toasts;
export const selectIsLoading = (state: { notifications: NotificationsState }) =>
  state.notifications.isLoading;
export const selectFilters = (state: { notifications: NotificationsState }) =>
  state.notifications.filters;
export const selectGroups = (state: { notifications: NotificationsState }) =>
  state.notifications.groups;
export const selectUISettings = (state: {
  notifications: NotificationsState;
}) => state.notifications.ui;

export default notificationsSlice.reducer;
